//#region imports
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ModalComponent } from '@toro/common/shared/components/modal/modal.component';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { OrderStatus } from '@toro/common/shared/enum/orders/order-status.enum';
import { OrderType } from '@toro/common/shared/enum/orders/order-type.enum';
import { OrderSideEnum } from '@toro/common/shared/enum/orders/side.enum';
import { RolesUser } from '@toro/common/shared/enum/roles-user.enum';
import { TrackOriginEnum } from '@toro/common/shared/enum/segment/segment-origin.enum';
import { TrendPeriodEnum } from '@toro/common/shared/enum/stock-trends/trend-period.enum';
import { TrendTendencyEnum } from '@toro/common/shared/enum/stock-trends/trend-tendency.enum';
import { SuitabilityProfilesEnum } from '@toro/common/shared/enum/suitability/suitability-profiles.enum';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { IReRegistrationMessage } from '@toro/common/shared/interfaces/re-registration/re-resgistration.interface';
import { CustodyClientOrder } from '@toro/common/shared/models/custody/custody-client-order.model';
import { Order } from '@toro/common/shared/models/exchange/order.model';
import { Strategy } from '@toro/common/shared/models/exchange/strategy.model';
import { InstrumentLite } from '@toro/common/shared/models/quote/instrument-lite.model';
import { Roles } from '@toro/common/shared/models/roles/roles.enum';
import { StockCheckoutType } from '@toro/common/shared/models/stocks-exchange/stock-checkout.model';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { UserReRegistationService } from '@toro/common/shared/services/re-registration/user-re-registration.service';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import { StocksMarketAction } from '@toro/common/shared/state-management/stocks-market/stocks-market.action';

import { BrokerageModel } from '@toro/common/shared/enum/billing/brokerage-model.enum';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';
import { StockCardsComponent } from '../../../partials/stock-cards/stock-cards.component';
import { TrackingsService } from '../../../services/tracking.service';
import { CheckoutWrapperComponent } from '../../../shared/panels/checkout/wrapper/checkout-wrapper.component';
import { StocksExchangeCheckoutAction } from '../../../state-management/stocks-exchange-checkout/stocks-exchange-checkout.action';

//#endregion

@AutoUnsubscribe()
@Component({
    selector: 'market-card',
    templateUrl: './market-card.component.html',
    styleUrls: ['./market-card.component.scss'],
})
export class MarketCardComponent extends StockCardsComponent implements OnInit, OnDestroy {

    @ViewChild('modalUserRegisterCard', { static: true }) public modalUserRegisterCard: ModalComponent;

    @Input('stockDetails') public stockDetails: any;
    @Input() public needQuote: boolean = true;
    @Input() public isPermitedOperation: boolean = true;

    public instrument: InstrumentLite = null;
    public quote: any = null;
    public tendency: any = null;
    public logo: string = '';
    public clientInvestments: CustodyClientOrder[] = [];
    public strategiesInvestments: any[] = [];
    public isFuture: boolean = false;
    public isOption: boolean = false;
    public isFractionary: boolean = false;
    public isLogged: boolean = false;
    public unitPrice: number = 0;
    public variation: number = 0;
    public trendPeriodEnum: any = TrendPeriodEnum;
    public trendTendencyEnum: any = TrendTendencyEnum;
    public waiting: boolean = false;
    public userRole: number = null;
    public suitabilityProfile: any = null;
    public symbol: string = '';
    public dateTimeLastTrade: string;
    public debugMode$: Observable<boolean>;
    public userIsExpired: boolean;
    public modalUserExpired: IReRegistrationMessage;

    private quoteSubscription: Subscription;
    private authSubscription: Subscription;
    private userSubscription: Subscription;
    private confirmationModal: Subscription;
    private trendsSubscription: Subscription;
    private instrumentSubscription: Subscription;
    private confirmUserLoggedSubscription: Subscription;
    private clientInvestmentsSubscriptions: Subscription;
    private suitabilityProfileSubscription: Subscription;
    private confirmSuitabilitySubscription: Subscription;
    private expiredSuitabilitySubscription: Subscription;
    private confirmAlredyHaveInvestmentsSubscription: Subscription;
    private confirmAlredyHaveInvestmentsBothSubscription: Subscription;
    private disposableSubscriptions: Subscription[] = [];
    private usingZeroModel: boolean = false;

    constructor(
        protected router: Router,
        protected dialogsServices: DialogsService,
        protected store: Store<AppState>,
        protected changeDetectorRef: ChangeDetectorRef,
        public settingsService: SettingsService,
        private userReRegistationService: UserReRegistationService,
        private tracking: TrackingsService,
    ) {
        super(router, dialogsServices, store, changeDetectorRef);

        // Recupera dos dados de authentication
        this.authSubscription = this.store.select((s) => s.authentication.currentUser)
            .filter((user) => user)
            .map((user) => user.role)
            .distinctUntilChanged()
            .subscribe((role) => {
                this.isLogged = true;
                if (role !== Roles.Identified) {
                    // this.store.dispatch({ type: StocksExchangeWalletAction.getInvestments });
                }
            });

        // Recupera os dados do perfil do usuário
        this.userSubscription = this.store.select((s) => s.userProfile.user)
            .filter((user: any) => user && user.profile ? true : false)
            .subscribe((user) => {
                this.userRole = user.profile;
            });

        this.debugMode$ = this.store.select((s) => s.utils.debugModeEnabled);
    }

    public ngOnInit() {
        this.setBase(this.stockDetails);
        // Recupera o perfil suitability do usuário
        this.suitabilityProfileSubscription = this.store.select((s) => s.suitability.profile)
            .filter((f) => f ? true : false)
            .subscribe((data) => {
                this.suitabilityProfile = data;
            });

        // Recupera as posições do usuário
        this.clientInvestmentsSubscriptions = this.store.select((s) => s.posrisk.custodies)
            .filter((custodies) => custodies.length ? true : false)
            .debounceTime(300)
            .subscribe((custodies) => {
                this.clientInvestments = custodies;
            });

        // Recupera as estratégias do usuário
        this.clientInvestmentsSubscriptions = this.store.select((s) => s.stocksExchange.wallet.validStrategies)
            .filter((strategies) => strategies ? true : false)
            .do((strategies) => this.waiting = true)
            .map((strategies) => this.filterUserStrategies(strategies))
            .do((strategies) => this.waiting = false)
            .filter((strategies) => strategies.length ? true : false)
            .subscribe((strategies) => {
                this.strategiesInvestments = strategies;
            });

        // Recupera a cotação do ativo
        this.quoteSubscription = this.store.select((s) => s.stocksExchange.stocksMarket.quote)
            .filter((f) => f[this.stockDetails.isCached ? this.stockDetails.code : this.symbol])
            .map((m) => m[this.stockDetails.isCached ? this.stockDetails.code : this.symbol])
            .subscribe((data) => {
                this.quote = data;
                this.unitPrice = this.quote.PriceLastTrade || this.quote.OpeningPrice || this.quote.ClosingPricePreviousDay;
                this.dateTimeLastTrade = this.quote.DateTimeLastTrade;
                this.variation = this.quote.Variation;
            });

        this.bindBillingSubscriptions()

        if (this.needQuote) {
            this.store.dispatch({
                type: StocksMarketAction.getQuote, payload: this.stockDetails.isCached ? this.stockDetails.code : this.symbol
            });
        }

        this.getUserRegistrationStore();
    }


    public bindBillingSubscriptions(){
        this.disposableSubscriptions.push(
            this.store.select((state) => state.billing)
                .subscribe((billing: any) => {
                    if (billing && billing.brokerageModel.type) {
                        this.usingZeroModel = (billing.brokerageModel.type === BrokerageModel.Zero);
                    }
                }),
        )
    }

    /**
     * Metodo para comprar uma ação
     */
    public buy() {
        this.tracking.sendMetricsToMixpanel(ActionEventsEnum.ELEMENT_CLICKED, {
            'Element': 'Botão comprar',
            'Product Area Origin': 'Bolsa',
            'Step': 'Explore o mercado',
            'Time Responsável': 'Roebling',
            'specificProperties': {
              'Ticker': this.symbol,
              'Market Type': this.isFuture ? "Future" : "Bovespa",
            }
          });

        const isIdentified = this.userRole === RolesUser.Identified;
        if (!this.isLogged || isIdentified) return this.goToRegister();
        if (!this.suitabilityProfile) return;
        const isConservative: boolean = this.suitabilityProfile.profile === SuitabilityProfilesEnum.Conservative;
        const isSuitabilityExpired: boolean = this.suitabilityProfile.isExpired;
        if (isConservative) {
            this.confirmSuitabilityProfile();
        } else if (isSuitabilityExpired) {
            this.suitabilityExpiredDisclaimer();
        } else {
            const userHasInvestments = this.checkIfUserHaveInvestments();
            if (userHasInvestments.sell && userHasInvestments.buy) {
                this.confirmAlredyHaveInvestmentsBothSides(OrderSideEnum.Compra);
            } else if (userHasInvestments.sell && !userHasInvestments.buy) { // sell
                const options = [
                    { id: 'manage', value: 'manage', text: 'Gerenciar meus investimentos' },
                    { id: 'new', value: 'new', text: 'Criar um novo investimento' },
                ];
                const title = `Você possui ${this.symbol} vendid${this.isFuture ? 'os' : 'as'}`;
                const content = `Você clicou em comprar mas já possui um investimento 'vendido' de ${this.symbol}. O que deseja fazer?`;
                this.confirmAlredyHaveInvestments(options, title, content, true);
            } else {
                this.goToCheckoutDirect(true, this.isOption);
            }
        }
    }

    /**
     * Metodo para vender uma ação
     */
    public sell() {
        this.tracking.sendMetricsToMixpanel(ActionEventsEnum.ELEMENT_CLICKED, {
            'Element': 'Botão vender',
            'Product Area Origin': 'Bolsa',
            'Step': 'Explore o mercado',
            'Time Responsável': 'Roebling',
            'specificProperties': {
              'Ticker': this.symbol,
              'Market Type': this.isFuture ? "Future" : "Bovespa",
            }
          });

        const isIdentified = this.userRole === RolesUser.Identified;
        if (!this.isLogged || isIdentified) return this.goToRegister();
        if (!this.suitabilityProfile) return;
        const isConservative = this.suitabilityProfile.profile === SuitabilityProfilesEnum.Conservative;
        if (isConservative) {
            return this.confirmSuitabilityProfile();
        } else {
            const userHasInvestments = this.checkIfUserHaveInvestments();
            if (this.isOption && userHasInvestments.buy) {
                return this.confirmationModal = this.dialogsServices.confirmAction(
                    `Você possui ${this.symbol} compradas`,
                    'Acesse a área de Meus Investimentos para vender as opções que já possui.',
                    'Acessar Meus Investimentos',
                    'Cancelar',
                )
                    .subscribe((data) => {
                        if (data) {
                            this.router.navigateByUrl(`/meus-investimentos`);
                        }
                    });
            } else if (this.isOption && !userHasInvestments.buy) {
                return this.dialogsService.showSimpleModal(
                    'icon_alert',
                    'Você não possui nenhuma opção para vender.',
                    'No caso de opções, você não pode entrar vendido no mercado.',
                    'Continue');
            } else if (userHasInvestments.sell && userHasInvestments.buy) {
                return this.confirmAlredyHaveInvestmentsBothSides(OrderSideEnum.Venda);
            } else if (!userHasInvestments.sell && userHasInvestments.buy) {
                const options = [
                    { id: 'manage', value: 'manage', text: `Gerenciar meus investimentos` },
                    { id: 'new', value: 'new', text: 'Criar um novo investimento' },
                ];
                const title = `Você possui ${this.symbol} comprad${this.isFuture ? 'os' : 'as'}`;
                // tslint:disable-next-line:max-line-length
                const content = `Você clicou em vender mas já possui ${this.isFuture ? 'contratos' : 'ações'} de ${this.symbol}. O que deseja fazer?`;
                return this.confirmAlredyHaveInvestments(options, title, content, false);
            } else {
                return this.goToCheckoutDirect(false, this.isOption);
            }
        }
    }

    /**
     * Manda para a tela de análise
     */
    public redirectAnalisys() {
        this.router.navigateByUrl(`/analise/${this.symbol}-cotacao-${this.treatsInstrumentDescription(this.instrument.description)}`);

        this.tracking.sendMetricsToMixpanel(ActionEventsEnum.ELEMENT_CLICKED, {
            'Element': 'Link ver análise completa',
            'Product Area Origin': 'Bolsa',
            'Step': 'Explore o mercado',
            'Time Responsável': 'Roebling',
            'specificProperties': {
              'Ticker': this.symbol,
              'Market Type': this.isFuture ? "Future" : "Bovespa",
            }
          });
    }

    public ngOnDestroy() {
        if (this.needQuote) {
            this.store.dispatch({ type: StocksMarketAction.unsubscribeQuotes, payload: [this.symbol] });
        }
    }

    public modalContent() {
        if (this.userIsExpired) {
            this.modalUserRegisterCard.show();
        }
    }

    /**
     * Função para mandar par o checkoout
     * @param isPurchase Se é compra ou venda
     * @param isOption Se é uma opção
     */
    private goToCheckoutDirect(isPurchase: boolean, isOption: boolean) {
        const item = new StockCheckoutType();
        item.symbol = this.symbol;
        item.isFuture = this.isFuture;
        item.purchase = isPurchase;
        item.origin = TrackOriginEnum.EXPLORE_MARKET;
        this.store.dispatch({ type: StocksExchangeCheckoutAction.setStockCheckout, payload: item });
      
        if (this.usingZeroModel) {
            this.dialogsServices.showComponentAsAsidePanel(CheckoutWrapperComponent, true);
        } else {
            const url = this.isFractionary || isOption ? '/checkout' : '/checkout/planejado';
            this.router.navigateByUrl(url);
        }
    }

    /**
     * Verifica se o usuário tem investimentos
     */
    private checkIfUserHaveInvestments(): any {
        return {
            buy: (this.clientInvestments.some((order) => order.total > 0 && order.symbol === this.symbol) ||
                this.strategiesInvestments.some((strategy) => strategy.side === OrderSideEnum.Compra && strategy.symbol === this.symbol)),
            sell: (this.clientInvestments.some((order) => order.total < 0 && order.symbol === this.symbol) ||
                this.strategiesInvestments.some((strategy) => strategy.side === OrderSideEnum.Venda && strategy.symbol === this.symbol)),
        };
    }

    /**
     * Modal de confirmação para usuários sem perfil de suitability.
     */
    private confirmSuitabilityProfile(): Subscription {

        this.confirmSuitabilitySubscription = this.dialogsServices.confirmAction(
            'Você não possui perfil para essa operação',
            'Você não possui perfil adequado para essa operação, o que deseja fazer ?',
            'Refazer Perfil',
            'Cancelar',
        )
            .subscribe((data) => {
                if (data) {
                    this.router.navigateByUrl('/perfil');
                }
            });

        return this.confirmSuitabilitySubscription;
    }

    /**
     * Modal de aviso ao usuario sobre o perfil de investir expirado
     */
    private suitabilityExpiredDisclaimer(): Subscription {
        this.expiredSuitabilitySubscription = this.dialogsServices.confirmAction(
            'Seu Perfil de Investidor expirou',
            'Para continuar investindo, você precisa refazer o questionário e atualizar seu perfil de investidor.',
            'Refazer questionário',
            'Cancelar',
        )
            .subscribe((data) => {
                if (data) {
                    this.router.navigateByUrl('/perfil');
                }
            });

        return this.expiredSuitabilitySubscription;
    }

    /**
     * Redireciona para o cadastroxa
     */
    private goToRegister(): void {
        window.open(this.settingsService.getConfigurationKeyValue('rau'), "_self");
    }

    /**
     * Abre modal de confirmação em caso de usuário com investimentos, com opções
     * @param options opções a serem exibidas
     * @param title titulo do modal
     * @param content texto de conteudo do modal
     * @param isPurchase Se é uma compra
     */
    private confirmAlredyHaveInvestments(options: any[], title: string, content: string, isPurchase: boolean): Subscription {
        this.confirmAlredyHaveInvestmentsSubscription = this.dialogsServices.confirmActionWithOptions(
            title,
            content,
            options,
            'Cancelar',
            'Confirmar',
        )
            .subscribe((data) => {
                if (data) {
                    if (data === 'manage') {
                        this.router.navigateByUrl('/meus-investimentos');
                    } else if (data === 'new') {
                        this.goToCheckoutDirect(isPurchase, false);
                    }
                }
            });

        return this.confirmAlredyHaveInvestmentsSubscription;
    }

    /**
     * Abre modal de confirmação, com opções quando o usuário possui posições compradas e vendidas
     * @param sideTrigger Se o usuário está tentando comprar ou vender.
     */
    private confirmAlredyHaveInvestmentsBothSides(sideTrigger: OrderSideEnum): Subscription {
        const options = [
            { id: 'new', value: 'new', text: 'Criar um novo investimento' },
            { id: 'manage', value: 'manage', text: 'Gerenciar meus investimentos' },
        ];
        this.confirmAlredyHaveInvestmentsBothSubscription = this.dialogsServices.confirmActionWithOptions(
            `Você possui ${this.symbol} ${this.isFuture ? 'comprados e vendidos' : 'compradas e vendidas'}`,
            // tslint:disable-next-line:max-line-length
            `Você clicou em ${sideTrigger === OrderSideEnum.Compra ? 'comprar' : 'vender'} mas já possui ${this.isFuture ? 'contratos' : 'ações'} de ${this.symbol}. O que deseja fazer?`,
            options,
            'Cancelar',
            'Confirmar',
        )
            .subscribe((data) => {
                if (data) {
                    if (data === 'manage') {
                        this.router.navigateByUrl('/meus-investimentos');
                    } else if (data === 'new') {
                        this.goToCheckoutDirect(sideTrigger === OrderSideEnum.Compra, false);
                    }
                }
            });

        return this.confirmAlredyHaveInvestmentsBothSubscription;
    }

    /**
     * Método que trata como a descrição do instrumento.
     *
     * @param description descrição do instrumento
     */
    private treatsInstrumentDescription(description: string) {
        return description.split(/\s|\//)
            .join('-');
    }

    /**
     * Filtra as estratégias executados do usuário
     * @param investments Estratégias do usuário
     */
    private filterUserStrategies(investments: Strategy[]): any[] {

        const investmentsArray: Strategy[] = Object.keys(investments)
            .map((strategy) => investments[strategy]);

        const investmentsWithoutExecutedLeg: any = investmentsArray.map((strategy) => {
            const legs: Order[] = strategy.legs;
            const inputLeg = legs.find((leg) => leg.internalOrderType === OrderType.Input);
            const stopGainLeg = legs.find((leg) => leg.internalOrderType === OrderType.StopGain);
            const stopLossLeg = legs.find((leg) => leg.internalOrderType === OrderType.StopLoss);

            if (!inputLeg || !stopGainLeg || !stopLossLeg) return false;

            if (inputLeg.status === OrderStatus.Filled || inputLeg.status === OrderStatus.PartiallyFilled) {
                if (stopGainLeg.status === OrderStatus.PendingTrigger && stopLossLeg.status === OrderStatus.PendingTrigger) {
                    return { ...inputLeg, ...{ symbol: strategy.symbol } };
                } else {
                    return null;
                }
            } else {
                return null;
            }

        })
            .filter((strategy) => strategy);
        return investmentsWithoutExecutedLeg;
    }

    private setBase(stockDetails: any) {
        if (stockDetails.isCached) {
            this.store.dispatch({ type: StocksMarketAction.getInstrumentInfo, payload: stockDetails.code });
            this.store.select((state) => state.stocksExchange.stocksMarket.instruments)
                .subscribe((instrument) => {
                    if (instrument[stockDetails.code]) {
                        this.populateMarketCard(stockDetails.isCached, instrument[stockDetails.code]);
                    }
                });
        } else {
            this.populateMarketCard(stockDetails.isCached, stockDetails);
        }
    }

    private populateMarketCard(isCached, details) {
        this.instrument = isCached ? new InstrumentLite(details) : details;
        this.symbol = isCached ? details.code : details.active;
        this.logo = isCached ? details.logo.content : details.logoPath;
        this.unitPrice = isCached ? details.priceLastTrade : details.unitPrice;
        this.variation = details.variation;
        this.isFuture = details.typeMarket === OrderMarketTypeEnum.Future;
        this.isOption = details.typeMarket === OrderMarketTypeEnum.Option;
        this.isFractionary = this.isFractionaryCondition(this.symbol, details);
    }

    private isFractionaryCondition(symbol: string, details: any): boolean {
        if (symbol === undefined) return false;

        return (this.symbol.endsWith('F') || details.typeMarket === OrderMarketTypeEnum.Fractionary) ||
            (details.typeMarket === OrderMarketTypeEnum.ETFFractionary && this.symbol.endsWith('F'));
    }

    /**
     * Busca os registros status do usuário
     */
    private getUserRegistrationStore() {
        this.store.select(({ userProfile }) => (userProfile))
            .pipe(take(2))
            .subscribe((up) => {
                if (!up.user || !+up.user.registrationStatus) { return; }
                this.verifyUserReRegister(+up.user.registrationStatus);
            });
    }

    /**
     * verifica se usuário está expirado
     * Expired = 15
     */
    private verifyUserReRegister(registrationStatus: number) {
        const userExpired = this.userReRegistationService.validateUserExpired(registrationStatus);
        if (!!userExpired) {
            this.userIsExpired = !!userExpired;
            this.modalUserExpired = userExpired;
            return;
        }
    }

}
